import { useEffect, useState } from 'react';

export interface ScrollPosition {
  x: number;
  y: number;
}

/**
 * Custom hook that tracks scroll position for effects like navbar background changes.
 * Uses a throttled scroll listener for performance optimization.
 *
 * @param throttleMs - Milliseconds to throttle scroll events (default: 50ms)
 * @returns Current scroll position { x, y }
 *
 * @example
 * const scrollPos = useScrollPosition();
 * const showNavBg = scrollPos.y > 10;
 *
 * return (
 *   <nav className={showNavBg ? 'with-bg' : ''}>
 *     Navigation
 *   </nav>
 * );
 */
export function useScrollPosition(throttleMs: number = 50): ScrollPosition {
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    x: 0,
    y: 0,
  });

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    let throttleTimer: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        setScrollPosition({
          x: window.scrollX,
          y: window.scrollY,
        });
        throttleTimer = null;
      }, throttleMs);
    };

    // Set initial position
    setScrollPosition({
      x: window.scrollX,
      y: window.scrollY,
    });

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [throttleMs, isClient]);

  return scrollPosition;
}
